import React, { useEffect, useRef, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { Send, MessageSquare, ArrowLeft, Info } from "lucide-react";
import { api } from "@/lib/api";
import { useApp } from "@/store";

export default function Chat() {
  const { user, t } = useApp();
  const [params, setParams] = useSearchParams();
  const activeId = params.get("c");
  const [convs, setConvs] = useState([]);
  const [thread, setThread] = useState(null);
  const [text, setText] = useState("");
  const endRef = useRef(null);

  const loadConvs = useCallback(async () => {
    try { const { data } = await api.get("/conversations"); setConvs(data); } catch {}
  }, []);

  const loadThread = useCallback(async (id) => {
    if (!id) { setThread(null); return; }
    try { const { data } = await api.get(`/conversations/${id}/messages`); setThread(data); }
    catch { setThread(null); }
  }, []);

  useEffect(() => { loadConvs(); }, [loadConvs]);
  useEffect(() => { loadThread(activeId); }, [activeId, loadThread]);

  // poll active thread every 3s
  useEffect(() => {
    if (!activeId) return;
    const iv = setInterval(() => { loadThread(activeId); loadConvs(); }, 3000);
    return () => clearInterval(iv);
  }, [activeId, loadThread, loadConvs]);

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [thread]);

  const send = async (e) => {
    e.preventDefault();
    if (!text.trim() || !activeId) return;
    const body = text;
    setText("");
    try {
      await api.post(`/conversations/${activeId}/messages`, { text: body });
      await loadThread(activeId);
      await loadConvs();
    } catch {}
  };

  return (
    <div className="mx-auto max-w-6xl px-5 py-8 sm:px-8">
      <h1 className="mb-6 flex items-center gap-2 font-display text-3xl font-extrabold tracking-tight"><MessageSquare className="text-[#002FA7]" /> {t("nav_messages")}</h1>
      <div className="grid gap-4 md:grid-cols-[320px_1fr]">
        {/* Conversation list */}
        <div className={`rounded-2xl border border-gray-200 ${activeId ? "hidden md:block" : ""}`} data-testid="conversation-list">
          {convs.length === 0 ? (
            <div className="p-6 text-sm text-gray-400" data-testid="conversations-empty">No conversations yet. Contact an owner from a listing to start chatting.</div>
          ) : convs.map((c) => (
            <button key={c.conversation_id} data-testid={`conv-${c.conversation_id}`}
              onClick={() => setParams({ c: c.conversation_id })}
              className={`flex w-full flex-col items-start gap-0.5 border-b border-gray-100 p-4 text-left transition-colors hover:bg-gray-50 ${activeId === c.conversation_id ? "bg-[#002FA7]/5" : ""}`}>
              <div className="flex w-full items-center justify-between">
                <span className="font-bold text-gray-900">{c.other_name}</span>
                <span className="rounded-full bg-gray-100 px-2 py-0.5 text-[10px] font-bold uppercase text-gray-500">{c.role}</span>
              </div>
              <span className="text-xs text-gray-500 line-clamp-1">{c.listing_title}</span>
              {c.last_message && <span className="mt-1 text-sm text-gray-400 line-clamp-1">{c.last_message}</span>}
            </button>
          ))}
        </div>

        {/* Thread */}
        <div className="flex min-h-[60vh] flex-col rounded-2xl border border-gray-200">
          {!thread ? (
            <div className="flex flex-1 items-center justify-center text-gray-400" data-testid="thread-empty">Select a conversation</div>
          ) : (
            <>
              <div className="flex items-center gap-3 border-b border-gray-100 p-4">
                <button onClick={() => setParams({})} className="md:hidden" data-testid="thread-back"><ArrowLeft size={18} /></button>
                <div>
                  <div className="font-bold text-gray-900" data-testid="thread-title">{thread.conversation.role === "owner" ? thread.conversation.renter_name : thread.conversation.owner_name}</div>
                  <div className="text-xs text-gray-500">{thread.conversation.listing_title}</div>
                </div>
              </div>

              <div className="flex-1 space-y-3 overflow-y-auto p-4" data-testid="messages">
                <div className="mx-auto flex max-w-md items-start gap-2 rounded-xl bg-[#FFF3CD] p-3 text-xs text-[#856404]">
                  <Info size={14} className="mt-0.5 shrink-0" /> Arrange pickup, delivery and any external payment method directly with the other party. RentGlobe does not process payments at this stage.
                </div>
                {thread.messages.map((m) => {
                  const mine = m.sender_id === user?.user_id;
                  return (
                    <div key={m.message_id} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
                      <div className={`max-w-[75%] rounded-2xl px-4 py-2 text-sm ${mine ? "bg-[#002FA7] text-white" : "bg-gray-100 text-gray-800"}`}>
                        {!mine && <div className="mb-0.5 text-[10px] font-bold uppercase opacity-60">{m.sender_name}</div>}
                        {m.text}
                      </div>
                    </div>
                  );
                })}
                <div ref={endRef} />
              </div>

              <form onSubmit={send} className="flex items-center gap-2 border-t border-gray-100 p-3">
                <input data-testid="message-input" value={text} onChange={(e) => setText(e.target.value)} placeholder="Type a message…"
                  className="flex-1 rounded-full border border-gray-200 px-4 py-2.5 outline-none focus:border-[#002FA7] focus:ring-2 focus:ring-[#002FA7]/20" />
                <button data-testid="message-send" type="submit" className="grid h-11 w-11 place-items-center rounded-full bg-[#FF3B30] text-white transition-colors hover:bg-[#E6352B]"><Send size={17} /></button>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
